import { t } from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { FaArrowDown } from 'react-icons/fa';
import { testIds } from 'src/app/constants';
import useSmoothScroll from 'src/app/hooks/useSmoothScroll';
import { IConversations } from 'src/types/serviceResponseTypes';
import styles from './ScrollButton.module.scss';

type PScroll = {
  conversations: IConversations;
};

/**
 * Scroll-to-bottom button: visible only while the conversation is scrolled up
 * from the bottom, and hides once the end is reached.
 */
function ScrollButton({ conversations }: PScroll) {
  const [smoothScroll] = useSmoothScroll();
  const [visible, setVisible] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const messageCount = conversations.messages?.length ?? 0;

  useEffect(() => {
    const container = document.getElementById(testIds.chatScreen);
    if (!container) return;

    // Distance from the bottom under which the view counts as "at the end".
    // Generous enough to absorb sub-pixel rounding and the sticky button's
    // own overflow, which slightly inflates scrollHeight.
    const BOTTOM_THRESHOLD = 24;

    const updateVisibility = () => {
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      setVisible(distanceFromBottom > BOTTOM_THRESHOLD);
    };

    updateVisibility();
    container.addEventListener('scroll', updateVisibility, { passive: true });
    window.addEventListener('resize', updateVisibility);

    return () => {
      container.removeEventListener('scroll', updateVisibility);
      window.removeEventListener('resize', updateVisibility);
    };
  }, [messageCount]);

  // logical stop after "Scroll to bottom" in the modal.
  useEffect(() => {
    if (visible) return;
    const button = buttonRef.current;
    if (!button || document.activeElement !== button) return;
    document.getElementById(testIds.promptInput)?.focus();
  }, [visible]);

  const scrollToBottom = () => smoothScroll();

  return (
    <div
      id={testIds.more_in_chat}
      className={`${styles.moreInChat} ${visible ? '' : styles.moreInChatHidden}`}
      aria-live='off'
      aria-atomic={false}
    >
      <button
        ref={buttonRef}
        type='button'
        className={styles.scrollBtn}
        onClick={scrollToBottom}
        aria-label={t('SCROLL_TO_BOTTOM')}
        aria-hidden={!visible}
        tabIndex={visible ? 0 : -1}
      >
        <FaArrowDown size={'0.85rem'} aria-hidden={true} focusable={false} />
      </button>
    </div>
  );
}

export default ScrollButton;
